/**
 * CLS (Cumulative Layout Shift) Optimizer
 * Fixes layout shifts caused by images, fonts, and dynamic content
 * 
 * @version 1.0.0
 */
(function() {
  'use strict';

  // Track layout shifts - use existing data if observer was registered inline
  let clsScore = window._clsScore || 0;
  let clsEntries = window._clsEntries || [];

  // Monitor CLS using PerformanceObserver
  // Note: Observer may already be registered inline in theme.liquid for early capture
  // If not registered yet, register it now
  if ('PerformanceObserver' in window && !window._clsObserverRegistered) {
    try {
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsScore += entry.value;
            clsEntries.push(entry);
            // Also update global if it exists
            if (window._clsScore !== undefined) {
              window._clsScore = clsScore;
            }
            if (window._clsEntries) {
              window._clsEntries = clsEntries;
            }
          }
        }
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
      window._clsObserverRegistered = true;
    } catch (e) {
      // Fallback for older browsers
    }
  } else if (window._clsObserverRegistered) {
    // Observer already registered inline - use window values as source of truth
    // Local variables are initialized but will be read from window in getScore/getEntries
    clsScore = window._clsScore || 0;
    clsEntries = window._clsEntries || [];
  }
  
  // Helper to get current CLS score (from window if inline observer is active)
  function getCurrentScore() {
    if (window._clsObserverRegistered && window._clsScore !== undefined) {
      return window._clsScore;
    }
    return clsScore;
  }
  
  // Helper to get current CLS entries (from window if inline observer is active)
  function getCurrentEntries() {
    if (window._clsObserverRegistered && window._clsEntries) {
      return window._clsEntries;
    }
    return clsEntries;
  }

  /**
   * Fix images without dimensions to prevent CLS
   */
  function fixImageDimensions() {
    const images = document.querySelectorAll('img:not([width]):not([height]), img[width="1"], img[height="1"]');
    
    images.forEach((img) => {
      // Skip if already processed
      if (img.dataset.clsFixed === 'true') return;
      
      // Skip logos - they should maintain their natural aspect ratio
      if (img.classList.contains('header__normal-logo') || 
          img.classList.contains('header__sticky-logo') || 
          img.classList.contains('header__mobile-logo') ||
          img.closest('.t4s-header__logo') ||
          img.closest('.kt-header__logo') ||
          img.closest('.header-logo')) {
        return;
      }
      
      // Wait for image to load to get natural dimensions
      if (img.complete && img.naturalWidth && img.naturalHeight) {
        setImageDimensions(img);
      } else {
        // Set dimensions when image loads
        img.addEventListener('load', function() {
          setImageDimensions(this);
        }, { once: true });
        
        // Fallback: set aspect ratio from CSS or default
        if (!img.style.aspectRatio) {
          const computedStyle = window.getComputedStyle(img);
          const aspectRatio = computedStyle.aspectRatio;
          
          if (aspectRatio && aspectRatio !== 'auto' && aspectRatio !== 'none') {
            img.style.aspectRatio = aspectRatio;
          } else {
            // Default aspect ratio based on common image types
            if (img.classList.contains('product-img') || img.closest('.product')) {
              img.style.aspectRatio = '1 / 1';
            } else if (img.classList.contains('hero') || img.closest('.hero')) {
              img.style.aspectRatio = '16 / 9';
            } else {
              img.style.aspectRatio = '4 / 3';
            }
          }
          
          img.style.objectFit = 'cover';
        }
      }
      
      img.dataset.clsFixed = 'true';
    });
  }

  /**
   * Set width and height attributes on image
   */
  function setImageDimensions(img) {
    // Skip logos - they should maintain their natural aspect ratio
    if (img.classList.contains('header__normal-logo') || 
        img.classList.contains('header__sticky-logo') || 
        img.classList.contains('header__mobile-logo') ||
        img.closest('.t4s-header__logo') ||
        img.closest('.kt-header__logo') ||
        img.closest('.header-logo')) {
      return;
    }
    
    if (img.naturalWidth && img.naturalHeight) {
      // Only set if not already set or if set to invalid values
      if (!img.hasAttribute('width') || img.getAttribute('width') === '1' || img.getAttribute('width') === '0') {
        img.setAttribute('width', img.naturalWidth);
      }
      if (!img.hasAttribute('height') || img.getAttribute('height') === '1' || img.getAttribute('height') === '0') {
        img.setAttribute('height', img.naturalHeight);
      }
      
      // Ensure aspect ratio is set using natural dimensions
      if (!img.style.aspectRatio) {
        img.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
      }
    }
  }

  /**
   * Fix background images to prevent CLS
   */
  function fixBackgroundImages() {
    const elements = document.querySelectorAll('[style*="background-image"], [data-bg-src]');
    
    elements.forEach((el) => {
      if (el.dataset.clsFixed === 'true') return;
      
      // Ensure container has dimensions
      if (!el.style.minHeight && !el.style.height) {
        const computedStyle = window.getComputedStyle(el);
        const aspectRatio = computedStyle.aspectRatio;
        
        if (!aspectRatio || aspectRatio === 'auto' || aspectRatio === 'none') {
          // Set default aspect ratio based on element type
          if (el.classList.contains('hero') || el.closest('.hero')) {
            el.style.aspectRatio = '16 / 9';
          } else if (el.classList.contains('banner') || el.closest('.banner')) {
            el.style.aspectRatio = '21 / 9';
          } else {
            el.style.aspectRatio = '4 / 3';
          }
        }
      }
      
      el.dataset.clsFixed = 'true';
    });
  }

  /**
   * Fix font loading to prevent layout shifts
   */
  function fixFontLoading() {
    // Ensure font-display: swap is set for all fonts
    const fontLinks = document.querySelectorAll('link[rel="stylesheet"][href*="font"], link[rel="preload"][as="font"]');
    
    fontLinks.forEach((link) => {
      // Add font-display: swap via CSS if not already present
      if (!document.getElementById('font-display-fix')) {
        const style = document.createElement('style');
        style.id = 'font-display-fix';
        style.textContent = `
          @font-face {
            font-display: swap;
          }
        `;
        document.head.appendChild(style);
      }
    });
  }

  /**
   * Fix ads and embeds to prevent CLS
   */
  function fixAdsAndEmbeds() {
    // Fix iframes without dimensions
    const iframes = document.querySelectorAll('iframe:not([width]):not([height])');
    iframes.forEach((iframe) => {
      if (!iframe.style.width && !iframe.style.height) {
        iframe.style.width = '100%';
        iframe.style.aspectRatio = '16 / 9';
      }
    });
    
    // Fix video elements
    const videos = document.querySelectorAll('video:not([width]):not([height])');
    videos.forEach((video) => {
      if (!video.style.width && !video.style.height) {
        video.style.width = '100%';
        video.style.aspectRatio = '16 / 9';
      }
    });
  }

  /**
   * Fix dynamic content that might cause layout shifts
   */
  function fixDynamicContent() {
    // Reserve space for elements that load dynamically
    const dynamicSelectors = [
      '.t4s-product-img',
      '.product__media',
      '.collection-item',
      '.cart-item',
      '.lazy-load',
      '[data-lazy]'
    ];
    
    dynamicSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => {
        if (!el.style.minHeight && !el.hasAttribute('data-cls-reserved')) {
          // Reserve minimum height
          const computedStyle = window.getComputedStyle(el);
          const height = computedStyle.height;
          
          if (height === '0px' || height === 'auto') {
            el.style.minHeight = '200px';
            el.setAttribute('data-cls-reserved', 'true');
          }
        }
      });
    });
  }

  /**
   * Initialize CLS optimization
   */
  function init() {
    // Run immediately for above-the-fold content
    fixImageDimensions();
    fixBackgroundImages();
    fixFontLoading();
    fixAdsAndEmbeds();
    
    // Run after DOM is ready for all content
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        fixImageDimensions();
        fixBackgroundImages();
        fixDynamicContent();
      });
    } else {
      fixImageDimensions();
      fixBackgroundImages();
      fixDynamicContent();
    }
    
    // Run after page load for any late-loading content
    window.addEventListener('load', function() {
      fixImageDimensions();
      fixBackgroundImages();
      fixDynamicContent();
    });
    
    // Watch for dynamically added content
    if ('MutationObserver' in window) {
      const observer = new MutationObserver(function(mutations) {
        let shouldFix = false;
        
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === 1) { // Element node
                if (node.tagName === 'IMG' || node.querySelector('img')) {
                  shouldFix = true;
                }
                if (node.style && node.style.backgroundImage) {
                  shouldFix = true;
                }
              }
            });
          }
        });
        
        if (shouldFix) {
          // Debounce fixes
          clearTimeout(window.clsFixTimeout);
          window.clsFixTimeout = setTimeout(function() {
            fixImageDimensions();
            fixBackgroundImages();
          }, 100);
        }
      });
      
      // Ensure document.body exists before observing
      function attachObserver() {
        if (document.body && document.body instanceof Node) {
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
          return true;
        }
        return false;
      }
      
      // Try to attach immediately
      if (!attachObserver()) {
        // If body doesn't exist, wait for DOM to be ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', function() {
            attachObserver();
          });
        } else {
          // DOM is already ready but body doesn't exist - wait a bit and retry
          setTimeout(function() {
            if (!attachObserver()) {
              // Fallback: try again after a short delay
              setTimeout(attachObserver, 100);
            }
          }, 0);
        }
      }
    }
  }

  // Initialize
  init();

  // Expose API for manual fixes
  window.KTCLSOptimizer = {
    fix: function() {
      fixImageDimensions();
      fixBackgroundImages();
      fixFontLoading();
      fixAdsAndEmbeds();
      fixDynamicContent();
    },
    getScore: function() {
      // BUG FIX: Always read from window if inline observer is active to get current values
      return getCurrentScore();
    },
    getEntries: function() {
      // BUG FIX: Always read from window if inline observer is active to get current values
      return getCurrentEntries();
    }
  };
})();

